import React from 'react';
import { ActivityIndicator, Pressable, StyleSheet, Text, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { reviewDesk } from '@/design-system';
import { useTranslation } from '@/hooks/useTranslation';
import { reportStyles } from './ReportView';
import type { ReportGateState } from './reportModel';

export interface ReportStateGateProps {
  state: Exclude<ReportGateState, 'report'>;
  onCheckAgain: () => void;
  onOpenSessions: () => void;
  onOpenChat?: () => void;
}

const STATE_COPY: Record<ReportStateGateProps['state'], { icon: keyof typeof MaterialIcons.glyphMap; title: string; body: string }> = {
  processing: { icon: 'hourglass-top', title: 'report.state.processingTitle', body: 'report.state.processingBody' },
  no_report: { icon: 'description', title: 'report.state.noReportTitle', body: 'report.state.noReportBody' },
  failed: { icon: 'error-outline', title: 'report.state.failedTitle', body: 'report.state.failedBody' },
  legacy: { icon: 'chat-bubble-outline', title: 'report.state.legacyTitle', body: 'report.state.legacyBody' },
};

/**
 * Everything the report route shows instead of a report (CORE-4): a running
 * analysis (auto-refreshing), a failed one, a session with no report yet, and
 * a pre-structured report that only exists as a chat message.
 */
export function ReportStateGate({ state, onCheckAgain, onOpenSessions, onOpenChat }: ReportStateGateProps) {
  const { t } = useTranslation();
  const copy = STATE_COPY[state];
  return (
    <View testID={`report_state_${state}`} style={[reportStyles.card, styles.card]}>
      {state === 'processing'
        ? <ActivityIndicator color={reviewDesk.accent} />
        : <MaterialIcons name={copy.icon} size={32} color={state === 'failed' ? '#B42318' : reviewDesk.accent} />}
      <Text style={[reportStyles.sectionTitle, styles.center]}>{t(copy.title)}</Text>
      <Text style={[reportStyles.body, styles.center]}>{t(copy.body)}</Text>
      <View style={styles.actions}>
        {state === 'legacy' && onOpenChat ? (
          <Pressable testID="report_state_open_chat" accessibilityRole="button" onPress={onOpenChat} style={reportStyles.primaryButton}>
            <Text style={reportStyles.primaryButtonText}>{t('report.state.openChat')}</Text>
          </Pressable>
        ) : (
          <Pressable testID="report_state_check_again" accessibilityRole="button" onPress={onCheckAgain} style={reportStyles.primaryButton}>
            <Text style={reportStyles.primaryButtonText}>{t('report.state.checkAgain')}</Text>
          </Pressable>
        )}
        <Pressable accessibilityRole="button" onPress={onOpenSessions} style={reportStyles.secondaryButton}>
          <Text style={reportStyles.secondaryButtonText}>{t('report.state.goToSessions')}</Text>
        </Pressable>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  card: { alignItems: 'center', paddingVertical: 32 },
  center: { textAlign: 'center', maxWidth: 520 },
  actions: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', gap: 10, marginTop: 8 },
});
